import { useEffect } from 'react';

import { useQuery } from '@apollo/client';
import { BehaviorSubject, from } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';

import { ACTIONS, type ActionType } from '@/views/B2BCheckout/context/types';

import { MAX_LICENSES } from '../constants';
import GET_PRICING_TEAM_PLAN from '../graphql/GetPricingTeamPlan.v4.b2b.graphql';
import type {
  GetPricingTeamPlan,
  GetPricingTeamPlanVariables,
} from '../graphql/types/GetPricingTeamPlan';

const _licenceCountBehavior = new BehaviorSubject<number>(0);
const licenceCountObservable$ = _licenceCountBehavior.asObservable();

interface usePricingTeamPlanProps {
  seats: number;
  ID: string;
  dispatch: React.Dispatch<ActionType>;
}

const usePricingTeamPlan = ({
  seats,
  ID,
  dispatch,
}: usePricingTeamPlanProps) => {
  const {
    data: pricingData,
    loading,
    error,
    refetch,
  } = useQuery<GetPricingTeamPlan, GetPricingTeamPlanVariables>(
    GET_PRICING_TEAM_PLAN,
    {
      variables: {
        ID,
      },
      context: { clientName: 'v4.b2b' },
      skip: !ID || seats < 0 || seats > MAX_LICENSES,
      notifyOnNetworkStatusChange: true,
    },
  );

  useEffect(() => {
    _licenceCountBehavior.next(seats);
    const subject = licenceCountObservable$
      .pipe(
        debounceTime(200),
        distinctUntilChanged(),
        switchMap(() =>
          from(
            refetch({
              ID,
              seats,
            }),
          ),
        ),
      )
      .subscribe(({ data }) =>
        dispatch({
          type: ACTIONS.SET_TOTAL_PRICE,
          payload: data.b2bProductCurrent?.priceTotalSeats || 0,
        }),
      );

    return () => {
      subject.unsubscribe();
    };
  }, [ID, dispatch, refetch, seats]);

  const pricePerSeat = pricingData?.b2bProductCurrent?.pricePerSeat || 0;
  const currency = pricingData?.b2bProductCurrent?.currency || '';
  const title = pricingData?.b2bProductCurrent?.title || '';
  const priceTotalSeats = pricingData?.b2bProductCurrent?.priceTotalSeats || 0;
  const productName = pricingData?.b2bProductCurrent?.title || '';
  const productId = pricingData?.b2bProductCurrent?.originalId || '';

  return {
    pricePerSeat,
    currency,
    title,
    priceTotalSeats,
    loading,
    error,
    productName,
    productId,
  };
};

export default usePricingTeamPlan;
